// ── 507 Mechanical Movements — Data File ─────────────────────────────────────
//
// HOW TO ADD A MOVEMENT:
//   1. Export each part as its own GLB from your CAD tool → /public/models/507/
//   2. Name every moving mesh inside each GLB (e.g. "Pulley_Driver")
//   3. Add an entry below with a unique id
//   4. Use `urls` (array) to load MULTIPLE GLBs into the same viewer, e.g.:
//        urls: ["/models/507/pulley_left.glb", "/models/507/rope.glb"]
//      All meshes from all files are merged into one interactive group.
//      Legacy single-file entries can still use `url` (string) — both work.
//   5. Add a matching function in MovementRegistry.js with the same id
//
// GLB mesh naming conventions:
//   Rotating parts   →  Gear_A, Gear_B, Pulley_Driver, Pulley_Driven, Crank, Flywheel
//   Sliding parts    →  Slider, Piston_Rod, Connecting_Rod, Crosshead
//   Belt/rope parts  →  Belt, Rope_Path  (animate via texture offset, not rotation)
//
// ROPE TEXTURE TRICK (for movement #1 and similar):
//   - Model the rope path as a swept surface in your CAD tool
//   - UV-unwrap it so U runs along the rope length
//   - Apply a repeating rope texture (or a simple stripe) in Blender/your DCC
//   - Export as GLB, name the mesh "Rope_Path"
//   - In MovementRegistry m1, slideTexture("Rope_Path", speed) animates the offset
//   - This creates the illusion of the rope moving without physically simulating it

export const MOVEMENTS = [

    // ── Pulleys & Belts ───────────────────────────────────────────────────────
    {
        id: "m1",
        name: "Movement #1",
        number: 1,
        description: "Illustrates the transmission of power by simple pulleys and an open belt. In this case both of the pulleys rotate in the same direction",
        // Multi-model example: three separate GLBs combined in one viewer
        // Replace these paths with your actual exported files:
        //   pulley_left.glb  — the driver pulley (mesh name: "Pulley_Driver")
        //   pulley_right.glb — the driven pulley (mesh name: "Pulley_Driven")
        //   rope.glb         — the swept rope path (mesh name: "Rope_Path")
        urls: [
            "/models/507/one.glb"
        ],
        //category: "Pulleys & Belts",
        //group: "Fixed Pulleys",
        initialView: [0, 0, 8],
        initialZoom: 85,
        rotation: [-Math.PI / 2, 0, -0.92],
        rotationAxis: "z",
    },
    {
        id: "m2",
        name: "Movement #2",
        number: 2,
        description: "Differs from 1 in the substitution of a crossed belt for the open one. In this case the direction of rotation of the pulleys is reversed",
        // Multi-model example: three separate GLBs combined in one viewer
        // Replace these paths with your actual exported files:
        //   pulley_left.glb  — the driver pulley (mesh name: "Pulley_Driver")
        //   pulley_right.glb — the driven pulley (mesh name: "Pulley_Driven")
        //   rope.glb         — the swept rope path (mesh name: "Rope_Path")
        urls: [
            "/models/507/two.glb"
        ],
        //category: "Pulleys & Belts",
        //group: "Fixed Pulleys",
        initialView: [0, 0, 8],
        initialZoom: 85,
        rotation: [0, -0.92, 0],
        rotationAxis: "y",
    },
    {
        id: "m3",
        name: "Movement #3",
        number: 3,
        description: "A method of transmitting motion from a shaft at right angles to another, by means of guide-pulleys. There are two of these pulleys, side by side, one for each leaf of the belt",
        // Multi-model example: three separate GLBs combined in one viewer
        // Replace these paths with your actual exported files:
        //   pulley_left.glb  — the driver pulley (mesh name: "Pulley_Driver")
        //   pulley_right.glb — the driven pulley (mesh name: "Pulley_Driven")
        //   rope.glb         — the swept rope path (mesh name: "Rope_Path")
        urls: [
            "/models/507/three.glb"
        ],
        //category: "Pulleys & Belts",
        //group: "Fixed Pulleys",
        initialView: [0, 0, 8],
        initialZoom: 80,
        rotation: [0, -0.69, 0],
        rotationAxis: "y",
    },
    {
        id: "m4",
        name: "Movement #4",
        number: 4,
        description: "A method of transmitting motion from a shaft at right angles to another whose axis is in the same plane. This is shown with a crossed belt. An open belt may be used, but the crossed one is preferable, as it gives more surface of contact.",
        // Multi-model example: three separate GLBs combined in one viewer
        // Replace these paths with your actual exported files:
        //   pulley_left.glb  — the driver pulley (mesh name: "Pulley_Driver")
        //   pulley_right.glb — the driven pulley (mesh name: "Pulley_Driven")
        //   rope.glb         — the swept rope path (mesh name: "Rope_Path")
        urls: [
            "/models/507/four.glb"
        ],
        //category: "Pulleys & Belts",
        //group: "Fixed Pulleys",
        initialView: [0, 0, 8],
        initialZoom: 88, 
        rotation: [0.15, -0.60, 0],
        rotationAxis: "y",
    },
    {
        id: "m5",
        name: "Movement #5",
        number: 5,
        description: "Resembles 1, with the addition of a movable tightening pulley, B. When this pulley is pressed against the band to take up the slack, the belt transmits motion from one of the larger pulleys to the other; but when it is not, the belt is so slack as not to transmit motion.",
        // Multi-model example: three separate GLBs combined in one viewer
        // Replace these paths with your actual exported files:
        //   pulley_left.glb  — the driver pulley (mesh name: "Pulley_Driver")
        //   pulley_right.glb — the driven pulley (mesh name: "Pulley_Driven")
        //   rope.glb         — the swept rope path (mesh name: "Rope_Path")
        urls: [
            "/models/507/five.glb"
        ],
        //category: "Pulleys & Belts",
        //group: "Fixed Pulleys",
        initialView: [0, 0, 8],
        initialZoom: 85, 
        rotation: [0, -1.1, 0],
        rotationAxis: "y",
    },
];

// Build category → group → item hierarchy (same pattern as main portfolio)
//
// Movements with NO category AND NO group are treated as top-level flat entries —
// they appear directly in the bottom nav without being nested under "Other".
// Movements with a category (but no group) are still grouped under that category
// as a flat list. Only truly uncategorised + ungrouped items skip the hierarchy.
export function buildMovementHierarchy(movements) {
    const catMap = new Map();
    // Separate bucket for movements that have neither category nor group
    const topLevel = [];

    movements.forEach(m => {
        const hasCategory = m.category != null;
        const hasGroup = m.group != null;

        if (!hasCategory && !hasGroup) {
            // Goes straight to top-level — no wrapping category
            topLevel.push(m);
            return;
        }

        const cat = m.category ?? "Other";
        const grp = m.group ?? null;
        if (!catMap.has(cat)) catMap.set(cat, new Map());
        const grpMap = catMap.get(cat);
        if (!grpMap.has(grp)) grpMap.set(grp, []);
        grpMap.get(grp).push(m);
    });

    const categorised = Array.from(catMap.entries()).map(([catName, grpMap]) => {
        const groups = Array.from(grpMap.entries()).map(([grpName, items]) => ({
            name: grpName, items, hero: items[0],
        }));
        const isFlat = groups.length === 1 && groups[0].name === null;
        return { name: catName, groups, isFlat, isTopLevel: false };
    });

    // Top-level movements become individual entries: each is its own "category"
    // with isTopLevel=true so the nav can render them directly as model buttons.
    const topLevelEntries = topLevel.length > 0
        ? [{ name: null, groups: [{ name: null, items: topLevel, hero: topLevel[0] }], isFlat: true, isTopLevel: true }]
        : [];

    return [...categorised, ...topLevelEntries];
}